import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Eye, Users, MessageCircle, Globe, RefreshCw } from "lucide-react";

const PERIODS = [
  { label: "Today", days: 1 },
  { label: "7 Days", days: 7 },
  { label: "30 Days", days: 30 },
  { label: "90 Days", days: 90 },
  { label: "All Time", days: 0 },
];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="card-neon rounded-2xl p-5">
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 grid place-items-center rounded-xl" style={{ backgroundColor: `${color}1f`, color }}>
        <Icon size={22} />
      </div>
      <div>
        <div className="font-tech text-2xl font-bold text-white">{value?.toLocaleString?.() ?? value}</div>
        <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      </div>
    </div>
  </div>
);

const BarRow = ({ label, count, max, color }) => (
  <div className="flex items-center gap-3">
    <span className="w-24 shrink-0 text-xs text-gray-400 truncate" title={label}>{label}</span>
    <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${max ? (count / max) * 100 : 0}%`, backgroundColor: color }} />
    </div>
    <span className="w-10 text-right text-xs text-gray-300">{count}</span>
  </div>
);

const AnalyticsManager = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(14);

  const load = useCallback(async (days) => {
    const d = days ?? period;
    setLoading(true);
    try {
      const res = await api.get("/admin/analytics", { params: { days: d } });
      setData(res.data);
    } catch (e) {
      toast({ title: "Failed to load analytics", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);
  useEffect(() => { load(period); }, [load, period]);

  const changePeriod = (days) => setPeriod(days);

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2">
      {PERIODS.map((p) => (
        <button
          key={p.days}
          onClick={() => changePeriod(p.days)}
          className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${period === p.days ? "bg-[#00ff87] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold text-white">Analytics</h2>
          {filterBar}
        </div>
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00ff87]" size={32} /></div>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...data.dailyPageviews.map((d) => d.count));
  const maxTop = Math.max(1, ...data.topPages.map((t) => t.count));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold text-white">Analytics</h2>
        <div className="flex items-center gap-3">
          {filterBar}
          <button onClick={() => load(period)} className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-[#00ff87]"><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Visits" value={data.totalPageviews} color="#00ff87" />
        <StatCard icon={Users} label="Unique Visitors" value={data.uniqueVisitors} color="#00e5ff" />
        <StatCard icon={MessageCircle} label="WhatsApp Clicks" value={data.totalWhatsappClicks} color="#25D366" />
        <StatCard icon={Globe} label="Unique IPs" value={data.uniqueIPs} color="#ff2e97" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-[#0e0f1a] p-5">
          <h3 className="font-display font-semibold text-white mb-4">Visits {period > 0 ? `(last ${period} day${period > 1 ? "s" : ""})` : "(all time)"}</h3>
          {data.dailyPageviews.length === 0 ? (
            <p className="text-gray-500 text-sm py-6 text-center">No visits recorded yet.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-40">
              {data.dailyPageviews.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group">
                  <div className="w-full rounded-t bg-gradient-to-t from-[#00ff87]/40 to-[#00ff87] transition-all" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: 4 }} title={`${d.date}: ${d.count}`} />
                  <span className="text-[9px] text-gray-500 rotate-0">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0e0f1a] p-5">
          <h3 className="font-display font-semibold text-white mb-4">Top Pages</h3>
          {data.topPages.length === 0 ? (
            <p className="text-gray-500 text-sm py-6 text-center">No data yet.</p>
          ) : (
            <div className="space-y-3">
              {data.topPages.map((t) => (
                <BarRow key={t.path} label={t.path} count={t.count} max={maxTop} color="#00e5ff" />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[#25D366]/20 bg-[#0e0f1a] p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle size={18} className="text-[#25D366]" />
          <h3 className="font-display font-semibold text-white">WhatsApp Link Clicks</h3>
          <span className="ml-auto font-tech text-xl font-bold text-[#25D366]">{data.totalWhatsappClicks}</span>
        </div>
        <p className="text-gray-500 text-xs">Total number of times visitors clicked any WhatsApp link across the site.</p>
      </div>
    </div>
  );
};

export default AnalyticsManager;
